import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Grid } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormProps } from 'util/Form';
import { SublistQuestion } from 'store/types/ApplicationQuestion';
import { TableColumn } from 'store/types/Table';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Table from 'components/shared/Table';
import Add from '@mui/icons-material/Add';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import AddAddressItemModal from './AddAddressItemModal';
import QuestionType from 'store/enums/QuestionType';
import classNames from 'classnames';

import commonStyles from 'styles/common.module.scss';

interface AddressQuestionFormItemProps extends FormProps {
  id: string;
  fieldName: string;
  sublistQuestions?: SublistQuestion[];
}

interface SublistAnswerView {
  [key: string]: string;
  referenceKey: string;
}

const AddressQuestionFormItem: React.FunctionComponent<AddressQuestionFormItemProps> = ({
  id: parentId,
  fieldName,
  sublistQuestions,
  disabled,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const [list, setList] = useState<SublistAnswerView[]>([]);
  const [addItemModalOpen, setAddItemModalOpen] = useState<boolean>(false);
  const sublistAnswersView: Array<SublistAnswerView[]> = useWatch({ control, name: fieldName });

  useEffect(() => {
    const newList: SublistAnswerView[] = [];
    sublistAnswersView.forEach((item) =>
      item.forEach(({ answer, referenceKey, content, type }) => {
        const value = newList.find((item) => item.referenceKey === referenceKey);
        if (value) {
          value[content] = answer;
        } else {
          newList.push({
            referenceKey,
            [content]: answer,
            type,
          });
        }
      })
    );

    setList(newList);
  }, [sublistAnswersView, sublistQuestions]);

  const handleDeleteItem = useCallback(
    ({ referenceKey }: SublistAnswerView) =>
      () => {
        const newValues = getValues()[fieldName].filter(
          (item: SublistAnswerView[]) => item[0].referenceKey !== referenceKey
        );
        setValue(fieldName, newValues);
      },
    [fieldName, getValues, setValue]
  );

  const columns: Array<TableColumn<any>> = useMemo(() => {
    const result: Array<TableColumn<any>> = sublistQuestions?.length
      ? sublistQuestions.map(({ content, type }) => ({
          label: content,
          key: content,
          dataIndex: content,
          align: type === QuestionType.Checkbox ? 'center' : 'left',
          render: (_: any, record: SublistAnswerView) =>
            type === QuestionType.Checkbox ? (
              <Checkbox checked={record[content] === 'true'} disabled={true} />
            ) : (
              record[content]
            ),
        }))
      : [];

    result.push({
      label: 'Action',
      key: 'action',
      render: (_: any, record: SublistAnswerView) => (
        <Button
          size={'small'}
          variant={'outlined'}
          className={classNames(commonStyles.dangerButtonOutlined, { [commonStyles.hidden]: !!record['result'] })}
          onClick={handleDeleteItem(record)}
          disabled={disabled}
        >
          <DeleteOutlined />
        </Button>
      ),
    });

    return result;
  }, [disabled, handleDeleteItem, sublistQuestions]);

  const handleAddItemModalOpen = useCallback(() => {
    setAddItemModalOpen(true);
  }, []);

  const handleAddItemModalClose = useCallback(() => {
    setAddItemModalOpen(false);
  }, []);

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        <Grid {...defaultGridItemProps} id={`${parentId}-alert`} data-alert={'true'} style={{ display: 'none' }}>
          <Alert severity="error" className={commonStyles.alert} style={{ marginBottom: 1 }}>
            Answer is required.
          </Alert>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Button
            color={'secondary'}
            variant={'contained'}
            startIcon={<Add />}
            className={commonStyles.uppercase}
            onClick={handleAddItemModalOpen}
            disabled={disabled}
          >
            {'New Address'}
          </Button>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Table columns={columns} list={list} />
        </Grid>
      </Grid>
      <AddAddressItemModal
        onClose={handleAddItemModalClose}
        open={addItemModalOpen}
        questions={sublistQuestions || []}
        fieldName={fieldName}
      />
    </>
  );
};
export default AddressQuestionFormItem;
